import { Observable } from 'rxjs';
import { GisDataModel, GisFieldsEnum } from '@bo-client/shared/models/gisData';
import { AllAreaEnum, AreaTypeEnum } from '@bo-client/activation-management/shared/models/commissioningModel';
import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { ConfigService } from '@bo-client/config/config.service';
import { THNAutocompleteStrategy } from '@bo-client/shared/components/get-gis-data/tenant-implementations/thn-autocomplete-strategy';
import { SWPAutocompleteStrategy } from '@bo-client/shared/components/get-gis-data/tenant-implementations/swp-autocomplete-strategy';
import { AutocompleteStrategy } from '@bo-client/shared/components/get-gis-data/autocomplete.strategy';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { EviAutocompleteStrategy } from '@bo-client/shared/components/get-gis-data/tenant-implementations/evi-autocomplete-strategy';
import { AdressSenseServiceAutocompleteStrategy } from '@bo-client/shared/components/get-gis-data/tenant-implementations/adress-sense-service-autocomplete-strategy';
import {
  DEDICATED_GIS_TENANT_IDS,
  DedicatedGisTenantId
} from '@bo-client/shared/components/get-gis-data/dedicated-gis-integrations';

interface TenantStrategyImplementation {
  tenant: string;
  strategy: AutocompleteStrategy;
}

@Injectable({
  providedIn: 'root'
})
export class GisAddressService {
  implementations: TenantStrategyImplementation[] = new Array<TenantStrategyImplementation>();

  private httpClientWithoutInterceptors: HttpClient;

  constructor(
    public httpBackend: HttpBackend,
    public httpClient: HttpClient,
    public configService: ConfigService,
    public dataShareService: DataShareService
  ) {
    this.httpClientWithoutInterceptors = new HttpClient(httpBackend);

    const dedicatedStrategies: Record<DedicatedGisTenantId, AutocompleteStrategy> = {
      thn: new THNAutocompleteStrategy(this.httpClientWithoutInterceptors, configService),
      swp: new SWPAutocompleteStrategy(this.httpClientWithoutInterceptors, configService),
      evi: new EviAutocompleteStrategy(this.httpClientWithoutInterceptors, configService)
    };

    DEDICATED_GIS_TENANT_IDS.forEach((tenant) => {
      this.implementations.push({ tenant, strategy: dedicatedStrategies[tenant] });
    });

    this.implementations.push({
      tenant: 'fallback',
      strategy: new AdressSenseServiceAutocompleteStrategy(
        this.httpClientWithoutInterceptors,
        configService,
        dataShareService
      )
    });
  }

  get currentStrategy(): AutocompleteStrategy {
    const tenantImplementation = this.implementations.find(
      (ele) => ele.tenant === this.dataShareService.selectedTenant.id.toLowerCase()
    );

    return (
      tenantImplementation?.strategy || this.implementations.find((ele) => ele.tenant === 'fallback').strategy
    );
  }

  getCitySuggestions(val: string): Observable<GisDataModel[]> {
    return this.currentStrategy.getCitySuggestions(val);
  }

  getStreetSuggestions(selectedGisData: GisDataModel, val: string): Observable<GisDataModel[]> {
    return this.currentStrategy.getStreetSuggestions(selectedGisData, val);
  }

  getStreetNoSuggestions(selectedGisData: GisDataModel, val: string): Observable<GisDataModel[]> {
    const res = this.currentStrategy.getStreetNoSuggestions(selectedGisData, val);
    res.subscribe((data) => {
      console.log('getStreetNoSuggestions', data);
    });
    return res;
  }

  isAvailableArea(areaType: AreaTypeEnum, areaTypes: AreaTypeEnum[], selectedOption: GisDataModel): boolean {
    if (areaTypes && areaTypes.length > 0) {
      return areaTypes.some((at) => this.currentStrategy.isAvailableArea(at, selectedOption));
    }
    return this.currentStrategy.isAvailableArea(areaType, selectedOption);
  }

  evaluateAreaAvailability(
    areaType: AreaTypeEnum,
    areaTypes: AreaTypeEnum[],
    gisData: GisDataModel
  ): { areaAvailable: boolean; availableAreas: AllAreaEnum[] } {
    const areaAvailable = this.isAvailableArea(areaType, areaTypes, gisData);
    const availableAreas = Object.values(AllAreaEnum).filter((value) =>
      this.isAvailableArea(value.toUpperCase() as AreaTypeEnum, [], gisData)
    );
    return { areaAvailable, availableAreas };
  }

  loadSelectedGisData(
    fieldName: GisFieldsEnum,
    selectedOption: any,
    selectedGisData: GisDataModel
  ): GisDataModel {
    switch (fieldName) {
      case GisFieldsEnum.zipCode:
        selectedGisData = selectedOption;
        return selectedGisData;
      case GisFieldsEnum.street:
        selectedGisData.streetId = selectedOption.streetId;
        selectedGisData.street = selectedOption.street;
        if (selectedOption.sapDistrictNumber) {
          selectedGisData.sapDistrictNumber = selectedOption.sapDistrictNumber;
        }
        selectedGisData.availableAreas =
          selectedOption.availableAreas ??
          selectedOption.areas ??
          selectedOption.areasGeneral ??
          selectedOption.areasExists ??
          selectedOption.sparte_code ??
          selectedOption.originalData?.sparte_code ??
          selectedOption.originalData?.sparte?.code ??
          null;
        return selectedGisData;
      case GisFieldsEnum.streetNo:
        selectedGisData.houseNo = selectedOption.houseNo;
        selectedGisData.houseNoAddition = selectedOption.houseNoAddition;
        if (selectedOption.sapDistrictNumber) {
          selectedGisData.sapDistrictNumber = selectedOption.sapDistrictNumber;
        }
        if (selectedOption.district) {
          selectedGisData.district = selectedOption.district;
        }
        selectedGisData.availableAreas = selectedOption.availableAreas;
        return selectedGisData;
      case GisFieldsEnum.developmentArea:
        selectedGisData.developmentAreaName = selectedOption.name;
        return selectedGisData;
    }
  }
}
